import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  useTheme,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  Grow,
} from '@mui/material';
import {
  LocationOn,
  LocalHospital,
  KeyboardArrowDown,
  Refresh,
  Star,
} from '@mui/icons-material';
import BookMyOpAPI from '../../services/BookMyOpapi';
import { config } from '../../config';
import HospitalCard from '../../components/HospitalCard';

const TopHospitals = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleHospitals, setVisibleHospitals] = useState(4);
  const [showingMore, setShowingMore] = useState(false);
  
  // Ref to prevent duplicate calls in React Strict Mode
  const hasFetched = useRef(false);

  // Fetch top-rated providers data from API
  const fetchHospitals = useCallback(async () => {
    // Prevent duplicate calls in React Strict Mode
    if (hasFetched.current) {
      return;
    }
    
    try {
      setLoading(true);
      hasFetched.current = true;
      
      const response = await BookMyOpAPI.getTopRatedProviders({ 
        limit: 20,
        minRating: 4.0
      });
      
      if (response && response.data && response.data.providers) {
        // Get first 12 hospitals for the grid
        const hospitalData = response.data.providers.slice(0, 12);
        setHospitals(hospitalData);
        setError(null);
      } else {
        setError('No top-rated hospitals found');
        setHospitals([]);
      }
    } catch (err) {
      setError('Failed to fetch top-rated hospitals. Please try again later.');
      console.error('Error fetching top-rated providers:', err);
      hasFetched.current = false; // Reset on error to allow retry
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load - with protection against React Strict Mode
  useEffect(() => {
    if (hospitals.length === 0) {
      fetchHospitals();
    }
  }, [hospitals.length, fetchHospitals]);

  const handleHospitalClick = (hospitalId) => {
    navigate(`/hospitals/${hospitalId}`);
  };

  const handleViewDoctors = (hospitalId) => {
    // Navigate to find doctors with hospital ID as provider filter
    navigate(`/find-doctors?provider=${hospitalId}`);
  };

  const handleShowMore = () => {
    setShowingMore(true);
    setTimeout(() => {
      setVisibleHospitals(prev => Math.min(prev + 4, hospitals.length));
      setShowingMore(false);
    }, 300);
  };

  const handleRefresh = () => {
    setVisibleHospitals(4);
  };

  if (loading) {
    return (
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: '#ffffff' }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress size={50} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: '#ffffff' }}>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  if (hospitals.length === 0) {
    return (
      <Box sx={{ py: { xs: 3, md: 4, },mt:2, bgcolor: '#ffffff' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No hospitals available at the moment.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: theme.palette.background.default }}>
      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mb: { xs: 2, md: 3 }, px: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: theme.palette.text.primary,
              textTransform:'uppercase',
              textAlign:'center',
            }}
          >
            Best Hospitals
          </Typography>
          
          {visibleHospitals > 4 && (
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{
                bgcolor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Stack>

        {/* Responsive Grid Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: { xs: 2, md: 2.5 },
            px: { xs: 1, md: 2 },
          }}
        >
          {hospitals.slice(0, visibleHospitals).map((hospital, index) => (
            <HospitalCard 
              key={hospital.id} 
              hospital={hospital} 
              index={index}
              onViewDoctors={handleViewDoctors}
            />
          ))}
        </Box>

        {/* Show More / View All Actions */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          {visibleHospitals < hospitals.length ? (
            <Button
              variant="contained"
              onClick={handleShowMore}
              disabled={showingMore}
              startIcon={showingMore ? <CircularProgress size={16} /> : <KeyboardArrowDown />}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.7rem', md: '0.75rem' },
                mr: 2,
                background: 'linear-gradient(45deg, theme.palette.primary.main, theme.palette.secondary.main)',
                '&:hover': {
                  background: 'linear-gradient(45deg, theme.palette.primary.main, theme.palette.secondary.main)',
                },
              }}
            >
              {showingMore ? 'Loading...' : `Show More (${hospitals.length - visibleHospitals} left)`}
            </Button>
          ) : null}
          
          <Button
            variant="outlined"
            onClick={() => navigate('/hospitals')}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.main,
                color: 'white',
              },
            }}
          >
            View All Hospitals
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default TopHospitals;
